// Shared free AI model catalog.
//
// The platform offers 8 free models. The backend exposes the live catalog at
// GET /api/models; this module ships a static copy as a fallback plus the
// display helpers (colors, legacy-id normalization) used across pages.

export const FREE_MODELS = [
  { id: "llama", name: "Llama 3.3 70B", provider: "Meta", desc: "Open-weights flagship — strong general assistant and conversation", tier: "free" },
  { id: "deepseek", name: "DeepSeek V3", provider: "DeepSeek", desc: "Excellent reasoning, coding and math at zero cost", tier: "free" },
  { id: "qwen", name: "Qwen 2.5 72B", provider: "Alibaba", desc: "Multilingual powerhouse with strong Asian-language support", tier: "free" },
  { id: "mistral", name: "Mistral Small 3.2", provider: "Mistral AI", desc: "Efficient European model with crisp, focused answers", tier: "free" },
  { id: "gemini", name: "Gemini 2.0 Flash", provider: "Google", desc: "Fast multimodal model with a broad knowledge base", tier: "free" },
  { id: "kimi", name: "Kimi K2", provider: "Moonshot AI", desc: "Long-context open model, great for documents and analysis", tier: "free" },
  { id: "glm", name: "GLM 4.5 Air", provider: "Z.AI", desc: "Bilingual agent-oriented model with solid tool use", tier: "free" },
  { id: "gptoss", name: "GPT-OSS 20B", provider: "OpenAI (open weights)", desc: "OpenAI's open-weights model — versatile general assistant", tier: "free" },
];

export const DEFAULT_MODEL_ID = "llama";

// Old paid/mock catalog ids that may still exist on stored assistants.
export const LEGACY_MODEL_MAP = {
  claude: "llama",
  chatgpt: "gptoss",
  perplexity: "deepseek",
  cohere: "mistral",
  groq: "llama",
  grok: "qwen",
  mercury2: "glm",
  pi: "gemini",
  manus: "kimi",
};

/** Map a (possibly legacy) model id onto the current free catalog id. */
export function normalizeModelId(id) {
  if (!id) return id;
  const key = String(id).toLowerCase().trim();
  return LEGACY_MODEL_MAP[key] || key;
}

/** Catalog entry for a (possibly legacy) model id, or null if unknown. */
export function getModelById(id) {
  const normalized = normalizeModelId(id);
  return FREE_MODELS.find((m) => m.id === normalized) || null;
}

/** Display name for a (possibly legacy) model id, falling back to the raw id. */
export function getModelName(id) {
  return getModelById(id)?.name || id;
}

// Card gradients (bg-gradient-to-br from-X to-Y) — one distinct hue per model.
export const MODEL_COLORS = {
  llama: "from-blue-500 to-indigo-600",
  deepseek: "from-cyan-500 to-blue-600",
  qwen: "from-violet-500 to-purple-600",
  mistral: "from-orange-500 to-amber-600",
  gemini: "from-teal-500 to-cyan-600",
  kimi: "from-rose-500 to-pink-600",
  glm: "from-slate-500 to-gray-600",
  gptoss: "from-emerald-500 to-green-600",
};

// Pill badge styles matching the existing "bg-X-50 text-X-600 border-X-200" pattern.
export const MODEL_BADGE_COLORS = {
  llama: "bg-blue-50 text-blue-600 border-blue-200",
  deepseek: "bg-cyan-50 text-cyan-600 border-cyan-200",
  qwen: "bg-violet-50 text-violet-600 border-violet-200",
  mistral: "bg-orange-50 text-orange-600 border-orange-200",
  gemini: "bg-teal-50 text-teal-600 border-teal-200",
  kimi: "bg-rose-50 text-rose-600 border-rose-200",
  glm: "bg-slate-50 text-slate-600 border-slate-200",
  gptoss: "bg-emerald-50 text-emerald-600 border-emerald-200",
};

/**
 * Fetch the live model catalog from the backend.
 *
 * Returns `{ models, configured, defaultModel, legacyMap, notice }` and falls
 * back to the static FREE_MODELS list (configured, no notice) on any error so
 * callers never have to special-case a failed request.
 */
export async function fetchModelCatalog(api) {
  try {
    const r = await api.get("/models");
    const data = r?.data;
    if (data && Array.isArray(data.models) && data.models.length > 0) {
      return {
        models: data.models,
        configured: data.configured !== false,
        defaultModel: data.default || DEFAULT_MODEL_ID,
        legacyMap: data.legacy_map || LEGACY_MODEL_MAP,
        notice: data.notice || null,
      };
    }
    throw new Error("Malformed model catalog response");
  } catch {
    return {
      models: FREE_MODELS,
      configured: true,
      defaultModel: DEFAULT_MODEL_ID,
      legacyMap: LEGACY_MODEL_MAP,
      notice: null,
    };
  }
}
